import React, { useState } from 'react';
import { FiX, FiEdit } from 'react-icons/fi';
import api from '../../utils/api';

const AddNoteModal = ({ ticketId, onClose, onSuccess }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Quick note templates
  const quickNotes = [
    "Called user, no answer. Will try again later.",
    "Called vendor, waiting for callback.",
    "User requested callback at specific time.",
    "Escalated to senior support for review.",
    "Pending vendor response for resolution.",
    "User confirmed issue resolved via callback."
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!note.trim()) {
      alert('Please enter a note');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/support/tickets/${ticketId}/note`, {
        note: note.trim()
      });

      if (response.data.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert(error.response?.data?.message || 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const insertQuickNote = (text) => {
    setNote(prev => prev ? `${prev}\n${text}` : text);
  };

  return (
    <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-yellow-50">
          <h3 className="text-lg font-semibold text-yellow-800">Add Internal Note</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-yellow-100 rounded-lg transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Info Banner */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-700">
              Internal notes are only visible to support agents. Users will not see these notes.
            </p>
          </div>

          {/* Quick Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Notes
            </label>
            <div className="flex flex-wrap gap-2">
              {quickNotes.map((text, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => insertQuickNote(text)}
                  className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200 transition-colors"
                >
                  {text}
                </button>
              ))}
            </div>
          </div>

          {/* Note Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add your internal note here..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
              required
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !note.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FiEdit size={16} />
                  Add Note
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNoteModal;
